import {GetCarQuoteCarouselBanner} from "./GetCarQuoteCarouselBanner";
import {
    IFalconConvenienceApiService,
    ADVANTAGES_PATH,
    CAROUSEL_BANNERS,
    MTA_EDIT_VEHICLE_DETAILS_PATH,
    ProductsEnum,
    Policy,
    Quote,
    productHelper,
    PartyPropertyEnum,
    Types
} from "@falcon/services";
import {GetHomeQuoteCarouselBanner} from "./GetHomeQuoteCarouselBanner";
import {AllianzAdvantagesCarouselBanner} from "./AllianzAdvantagesCarouselBanner";
import {BreakdownCarouselBanner} from "./BreakdownCarouselBanner";
import {lazyInject} from "@athena-portal/core";
import {Carousel, CarouselProps, CarouselBanner} from "@falcon/plugin-falcon-journey";
import {pushImpressionEvent} from "./carouselEvents";
import {MarketingPreferencesCarouselBanner} from "./MarketingPreferencesCarouselBanner";

interface DashboardCarouselProps extends CarouselProps {
    newCarQuotePopUp: any;
    newHomeQuotePopUp: any;
    quotes?: Quote[];
    marketingPreferenceCheckboxStates: Record<string, boolean>;
}

export class DashboardCarousel extends Carousel<DashboardCarouselProps> {
    @lazyInject(Types.FalconConvenienceApiService)
    protected readonly falconConvenienceApiService: IFalconConvenienceApiService

    private readonly HOME_AND_MOTOR = ProductsEnum.WOLF_MOTOR + ", " + ProductsEnum.HOME;
    private interactionIdForBreakdownBanner: string;

    public componentDidMount() {
        super.componentDidMount();
        this.handleSingleMotorPolicyWithoutBreakdown();
    }

    override getSettings(next?: ((value: any) => void)): void {
        this.falconConvenienceApiService.getSettings().subscribe(next);
    }

    protected override goToNext(banners: any[]) {
        super.goToNext(banners);
        pushImpressionEvent(this.state.currentIndex, this.state.carouselBanners);
    };

    protected override goToSlide(slideIndex: number) {
        super.goToSlide(slideIndex);
        pushImpressionEvent(this.state.currentIndex, this.state.carouselBanners);
    };

    // FAL-42264: per requirement max banner count is 5
    override getBanners(availableBannersConfig: string): CarouselBanner[] {
        const policies: Policy[] = this.props.policies || [this.props.policy];
        if (availableBannersConfig) {
            let availableBanners: CarouselBanner[] = this.setupBanners()
                .filter(entry => availableBannersConfig.includes(entry.name));

            availableBanners = this.filterBannersOnConditions(availableBanners, policies);

            return availableBanners;
        } else {
            return [];
        }
    }

    private filterBannersOnConditions(availableBanners: any[], policies: Policy[]): any[] {
        if (this.props.product) {
            availableBanners = availableBanners.filter(entry => entry.product.includes(this.props.product));
        }
        if (productHelper.includesNonExpiredAnnualHomePolicies(policies)) {
            availableBanners = availableBanners.filter(entry => entry.name !== CAROUSEL_BANNERS.getHomeQuote.name);
        }
        if (!this.interactionIdForBreakdownBanner) {
            availableBanners = availableBanners.filter(entry => entry.name !== CAROUSEL_BANNERS.breakdown.name);
        }
        if (!productHelper.atLeastOneActivePolicy(policies)) {
            availableBanners = availableBanners.filter(entry => entry.name !== CAROUSEL_BANNERS.allianzAdvantages.name);
        }
        if (this.props.marketingPreferenceCheckboxStates[PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL]) {
            availableBanners = availableBanners.filter(banner => banner.id !== CAROUSEL_BANNERS.marketingPreferences.id);
        }
        return availableBanners;
    }

    private handleSingleMotorPolicyWithoutBreakdown() {
        const policies: Policy[] = this.props.policies || [this.props.policy];
        const activeMotorPolicies: Policy[] = productHelper.findActiveAnnualMotorPolicies(policies);
        if (activeMotorPolicies.length === 1 && !activeMotorPolicies[0].interaction.optionalExtras.breakdownCover) {
            this.interactionIdForBreakdownBanner = activeMotorPolicies[0].interaction._id;
        }
    }

    private setupBanners(): CarouselBanner[] {
        return [
            {
                id: CAROUSEL_BANNERS.getCarQuote.id,
                product: this.HOME_AND_MOTOR,
                name: CAROUSEL_BANNERS.getCarQuote.name,
                link: `/journey/show/product/${ProductsEnum.WOLF_MOTOR}/process/nb`,
                component: <GetCarQuoteCarouselBanner {...this.props} quotes={this.props.quotes}/>,
            },
            {
                id: CAROUSEL_BANNERS.getHomeQuote.id,
                product: this.HOME_AND_MOTOR,
                name: CAROUSEL_BANNERS.getHomeQuote.name,
                link: `/journey/show/product/${ProductsEnum.HOME}/process/nb`,
                component: <GetHomeQuoteCarouselBanner {...this.props} quotes={this.props.quotes}/>,
            },
            {
                id: CAROUSEL_BANNERS.allianzAdvantages.id,
                product: this.HOME_AND_MOTOR,
                name: CAROUSEL_BANNERS.allianzAdvantages.name,
                link: ADVANTAGES_PATH,
                component: <AllianzAdvantagesCarouselBanner/>,
            },
            {
                id: CAROUSEL_BANNERS.breakdown.id,
                product: ProductsEnum.WOLF_MOTOR,
                name: CAROUSEL_BANNERS.breakdown.name,
                link: MTA_EDIT_VEHICLE_DETAILS_PATH + this.interactionIdForBreakdownBanner,
                component: <BreakdownCarouselBanner interaction_id={this.interactionIdForBreakdownBanner}/>,
            },
            {
                id: CAROUSEL_BANNERS.marketingPreferences.id,
                product: this.HOME_AND_MOTOR,
                name: CAROUSEL_BANNERS.marketingPreferences.name,
                link: "/dashboard/marketing-preferences",
                component: <MarketingPreferencesCarouselBanner/>,
            },
        ]
    }
}
